import { useState, type ImgHTMLAttributes } from "react";
import { Download, Maximize2 } from "lucide-react";
import { Image } from "@/components/Image";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";

const MIME_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
};

type Props = ImgHTMLAttributes<HTMLImageElement> & { node?: unknown };

function filenameFrom(src: string, alt: string | undefined, mime: string): string {
  try {
    const name = decodeURIComponent(
      new URL(src, window.location.origin).pathname.split("/").pop() ?? "",
    );
    if (name.includes(".")) return name;
  } catch {
    /* malformed or relative src */
  }
  const ext = MIME_EXT[mime] ?? "png";
  const base = (alt || "image").replace(/[^\w.-]+/g, "-").replace(/^-+|-+$/g, "") || "image";
  return `${base}.${ext}`;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadImage(src: string, alt?: string) {
  try {
    const blob = await (await fetch(src)).blob();
    saveBlob(blob, filenameFrom(src, alt, blob.type));
  } catch {
    window.open(src, "_blank", "noopener,noreferrer");
  }
}

const ACTION_BTN =
  "co-glass-control grid size-8 place-items-center rounded-pill text-co-faint transition-colors hover:text-co-text";

export default function ChatMarkdownImage({
  node: _node,
  src,
  alt,
  className: _className,
  width: _width,
  height: _height,
  ...rest
}: Props) {
  const [open, setOpen] = useState(false);
  if (!src) return null;

  const label = alt || "Image";

  return (
    <>
      <span className="relative my-2 inline-block max-w-full" data-streamdown="image-wrapper">
        <span className="relative flex aspect-square w-[300px] max-w-full items-center justify-center overflow-hidden rounded-card bg-co-surface-solid">
          <Image
            {...rest}
            src={src}
            alt={alt ?? ""}
            data-streamdown="image"
            className="size-full object-contain"
          />
        </span>
        <span className="absolute right-2 bottom-2 flex gap-1">
          <button
            type="button"
            className={ACTION_BTN}
            title="Download image"
            aria-label={`Download ${label}`}
            onClick={() => void downloadImage(src, alt)}
          >
            <Download className="size-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            className={ACTION_BTN}
            title="View fullscreen"
            aria-label={`View ${label} fullscreen`}
            onClick={() => setOpen(true)}
          >
            <Maximize2 className="size-3.5" strokeWidth={2} />
          </button>
        </span>
      </span>
      <KitModal
        isOpen={open}
        onOpenChange={setOpen}
        title={label}
        modalClassName="flex max-h-[90vh] w-full max-w-[min(92vw,1100px)] flex-col p-4"
      >
        <div className="overflow-hidden rounded-card bg-co-surface">
          <Image
            src={src}
            alt={label}
            className="mx-auto max-h-[75vh] w-full object-contain"
          />
        </div>
      </KitModal>
    </>
  );
}
